import React, { useState, useEffect } from 'react';
import { Box, TextField, useTheme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { DataGridPro, getGridStringOperators } from '@mui/x-data-grid-pro';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { getLpSuppliers } from '../../../../features/master-data';
import {
  getCarrierSupplierMap,
  addCarrierSupplierMap,
  updateCarrierSupplierMap,
  deleteCarrierSupplierMap,
} from '../../services/SettingsRepository';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import dayjs from 'dayjs';
import useSnackbar from '../../../../shared/hooks/useSnackbar';
import useRowEditing from '../../../../shared/hooks/useRowEditing';

// Transport medium constants extracted outside the component to avoid re-creation on every render
const TRANSPORT_MEDIUM_OPTIONS = [
  { value: 'S', label: 'Ship' },
  { value: 'B', label: 'Train' },
  { value: 'C', label: 'Container' },
  { value: 'L', label: 'Truck' },
  { value: 'U', label: 'Eigene Achse' },
];

const TRANSPORT_MEDIUM_MAP = TRANSPORT_MEDIUM_OPTIONS.reduce((acc, cur) => {
  acc[cur.value] = cur.label;
  return acc;
}, {});

const formatDateTime = (dateTime) =>
  dateTime ? dayjs(dateTime).local().format('YYYY-MM-DD HH:mm:ss') : 'N/A';

const getSupplierInfo = (supplier) => {
  const supplierNo = supplier?.supplierNo;
  const supplierName = supplier?.supplierName;
  if (!supplierNo)
    return { supplierNo: null, displayName: 'N/A', hasSupplierName: false };
  return {
    supplierNo,
    displayName: supplierName || 'N/A',
    hasSupplierName: !!supplierName,
  };
};

const extractMessageFromError = (error, defaultMessage) => {
  const prefix = 'Failed to parse JSON response. Response text: ';
  const msg = error?.message || '';
  return msg.startsWith(prefix) ? msg.slice(prefix.length) : defaultMessage;
};

// Custom string filter operators with a custom text extractor
const createCustomStringFilterOperators = (getTextFromParams) =>
  getGridStringOperators().map((op) => ({
    ...op,
    getApplyFilterFn: (filterItem) => {
      if (
        op.value === 'isAnyOf' &&
        (!filterItem.value ||
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0)
      ) {
        return null;
      }
      if (!filterItem.value && !['isEmpty', 'isNotEmpty'].includes(op.value))
        return null;

      return (params) => {
        const text = getTextFromParams(params);
        const filterValue =
          typeof filterItem.value === 'string'
            ? filterItem.value.toLowerCase()
            : '';
        switch (op.value) {
          case 'contains':
            return text.includes(filterValue);
          case 'equals':
            return text === filterValue;
          case 'startsWith':
            return text.startsWith(filterValue);
          case 'endsWith':
            return text.endsWith(filterValue);
          case 'doesNotContain':
            return !text.includes(filterValue);
          case 'doesNotEqual':
            return text !== filterValue;
          case 'isEmpty':
            return !text.trim();
          case 'isNotEmpty':
            return !!text.trim();
          case 'isAnyOf':
            return filterItem.value.some(
              (v) => text === (v || '').toLowerCase()
            );
          default:
            return false;
        }
      };
    },
  }));

const supplierStringFilterOperators = createCustomStringFilterOperators(
  (params) => {
    const { supplierNo, displayName } = getSupplierInfo(params);
    return supplierNo ? `${supplierNo} - ${displayName}`.toLowerCase() : '';
  }
);

const transportMediumStringFilterOperators = createCustomStringFilterOperators(
  (params) =>
    params && TRANSPORT_MEDIUM_MAP[params]
      ? `${params} - ${TRANSPORT_MEDIUM_MAP[params]}`.toLowerCase()
      : ''
);

const CarrierSupplierMap = () => {
  const [carrierSupplierMap, setCarrierSupplierMap] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const {
    rowModesModel,
    setRowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
  } = useRowEditing({
    rows: carrierSupplierMap,
    setRows: setCarrierSupplierMap,
  });
  const theme = useTheme();

  const fetchCarrierSupplierMap = async () => {
    try {
      const response = await getCarrierSupplierMap();
      setCarrierSupplierMap(
        response.map((item) => ({
          ...item,
          supplier: item.supplier || item.originalSupplier || null,
        }))
      );
    } catch (error) {
      showSnackbar('Error fetching carrierSupplierMap.', 'error');
    }
  };

  const fetchSuppliers = async () => {
    try {
      setSuppliers(await getLpSuppliers());
    } catch (error) {
      showSnackbar('Error fetching suppliers.', 'error');
    }
  };

  useEffect(() => {
    fetchCarrierSupplierMap();
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (id) => async () => {
    try {
      await deleteCarrierSupplierMap(id);
      await fetchCarrierSupplierMap();
      showSnackbar('Carrier ID / Supplier ID Mapping deleted successfully!');
    } catch (error) {
      showSnackbar('Error deleting Carrier ID / Supplier ID Mapping.', 'error');
    }
  };

  const canDelete = (id) => {
    const row = carrierSupplierMap.find((r) => r.id === id);
    if (!row) return false;
    return (
      carrierSupplierMap.filter(
        (r) => r.carrierId === row.carrierId && !r.isNew
      ).length > 1
    );
  };

  const handleCopyClick = (id) => () => {
    const currentRow = carrierSupplierMap.find((row) => row.id === id);
    const currentIndex = carrierSupplierMap.findIndex((row) => row.id === id);
    const newId = crypto.randomUUID();
    const newRow = {
      id: newId,
      carrierId: currentRow.carrierId,
      name: currentRow.name,
      originalSupplier: currentRow.originalSupplier,
      supplier: currentRow.originalSupplier,
      transportMedium: null,
      isHide: false,
      isNew: true,
    };
    setCarrierSupplierMap((prev) => [
      ...prev.slice(0, currentIndex + 1),
      newRow,
      ...prev.slice(currentIndex + 1),
    ]);
    setRowModesModel((prev) => ({
      ...prev,
      [newId]: { mode: GridRowModes.Edit },
    }));
  };

  const processRowUpdate = async (newRow) => {
    try {
      if (newRow.isNew) {
        await addCarrierSupplierMap(newRow);
        showSnackbar('Carrier ID / Supplier ID Mapping added successfully!');
      } else {
        await updateCarrierSupplierMap(newRow.id, newRow);
        showSnackbar('Carrier ID / Supplier ID Mapping updated successfully!');
      }
      await fetchCarrierSupplierMap();
    } catch (error) {
      showSnackbar(
        extractMessageFromError(
          error,
          'Error saving Carrier ID / Supplier ID Mapping.'
        ),
        'error'
      );
      if (newRow.isNew) {
        setCarrierSupplierMap((prev) =>
          prev.filter((row) => row.id !== newRow.id)
        );
      }
      throw error;
    }
    return { ...newRow, isNew: false };
  };

  const isSupplierDifferent = (params) =>
    params?.row?.originalSupplier?.supplierNo !==
    params?.row?.supplier?.supplierNo;

  const carrierSupplierMapColumns = [
    {
      field: 'carrierId',
      headerName: 'Carrier ID',
      width: 150,
      editable: false,
    },
    { field: 'name', headerName: 'Carrier Name', width: 300, editable: false },
    {
      field: 'originalSupplier',
      headerName: 'Original Supplier',
      width: 250,
      editable: false,
      filterOperators: supplierStringFilterOperators,
      renderCell: (params) => {
        const { supplierNo, displayName, hasSupplierName } = getSupplierInfo(
          params?.row?.originalSupplier
        );
        if (!supplierNo) return 'N/A';
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {!hasSupplierName && (
              <WarningAmberOutlinedIcon
                fontSize='small'
                style={{ marginRight: 4 }}
              />
            )}
            {`${supplierNo} - ${displayName}`}
          </span>
        );
      },
      sortComparator: (v1, v2) => (v1?.supplierNo ?? 0) - (v2?.supplierNo ?? 0),
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      width: 250,
      editable: true,
      filterOperators: supplierStringFilterOperators,
      renderCell: (params) => {
        const { supplierNo, displayName, hasSupplierName } = getSupplierInfo(
          params?.row?.supplier
        );
        if (!supplierNo) return 'N/A';
        return (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              ...(isSupplierDifferent(params)
                ? { color: theme.palette.error.main }
                : {}),
            }}
          >
            {!hasSupplierName && (
              <WarningAmberOutlinedIcon
                fontSize='small'
                style={{ marginRight: 4 }}
              />
            )}
            {`${supplierNo} - ${displayName}`}
          </span>
        );
      },
      renderEditCell: (params) => (
        <Autocomplete
          options={suppliers}
          getOptionLabel={(option) => {
            const { supplierNo, displayName } = getSupplierInfo(option);
            return supplierNo ? `${supplierNo} - ${displayName}` : 'N/A';
          }}
          value={params?.row?.supplier || null}
          isOptionEqualToValue={(option, value) =>
            value ? option.supplierNo === value.supplierNo : false
          }
          onChange={(event, newValue) => {
            params.api.setEditCellValue({
              id: params.id,
              field: 'supplier',
              value: newValue || null,
            });
          }}
          renderInput={(props) => (
            <TextField {...props} label='Supplier' sx={{ width: 230 }} />
          )}
        />
      ),
      sortComparator: (v1, v2) => (v1?.supplierNo ?? 0) - (v2?.supplierNo ?? 0),
    },
    {
      field: 'transportMedium',
      headerName: 'Transport Medium',
      width: 250,
      editable: true,
      filterOperators: transportMediumStringFilterOperators,
      renderCell: (params) =>
        params?.value && TRANSPORT_MEDIUM_MAP[params.value]
          ? `${params.value} - ${TRANSPORT_MEDIUM_MAP[params.value]}`
          : 'N/A',
      renderEditCell: (params) => (
        <Autocomplete
          options={TRANSPORT_MEDIUM_OPTIONS}
          getOptionLabel={(option) => `${option.value} - ${option.label}`}
          value={
            TRANSPORT_MEDIUM_OPTIONS.find(
              (option) => option.value === params.value
            ) || null
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={(event, newValue) => {
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newValue ? newValue.value : null,
            });
          }}
          renderInput={(props) => (
            <TextField
              {...props}
              label='Transport Medium'
              sx={{ width: 230 }}
            />
          )}
        />
      ),
    },
    {
      field: 'isHide',
      headerName: 'Is Hide',
      type: 'boolean',
      width: 150,
      editable: true,
    },
    {
      field: 'updated',
      headerName: 'Updated Time',
      width: 200,
      editable: false,
      valueFormatter: (params) => formatDateTime(params),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key='save'
              icon={<SaveIcon />}
              label='Save'
              sx={{ color: 'primary.main' }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key='cancel'
              icon={<CancelIcon />}
              label='Cancel'
              className='textPrimary'
              onClick={handleCancelClick(id)}
              color='inherit'
            />,
            <GridActionsCellItem
              key='placeholder'
              icon={<DeleteIcon />}
              label=''
              disabled
              sx={{ visibility: 'hidden' }}
              onClick={() => {}}
            />,
          ];
        }
        return [
          <GridActionsCellItem
            key='edit'
            icon={<EditIcon />}
            label='Edit'
            className='textPrimary'
            onClick={handleEditClick(id)}
            color='inherit'
          />,
          <GridActionsCellItem
            key='copy'
            icon={<ContentCopyIcon />}
            label='Copy'
            className='textPrimary'
            onClick={handleCopyClick(id)}
            color='inherit'
          />,
          <GridActionsCellItem
            key='delete'
            icon={<DeleteIcon />}
            label='Delete'
            onClick={handleDeleteClick(id)}
            color='inherit'
            disabled={!canDelete(id)}
          />,
        ];
      },
    },
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        pagination
        rows={carrierSupplierMap}
        columns={carrierSupplierMapColumns}
        getRowId={(row) => row.id}
        editMode='row'
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={() => {}}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          columns: { columnVisibilityModel: { carrierId: true } },
          filter: {
            filterModel: {
              items: [{ field: 'isHide', operator: 'is', value: 'false' }],
            },
          },
        }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

export default CarrierSupplierMap;
